/**
 * TCP Character Personalizer v2.5
 * Manifest-driven + existing mockup compositing.
 *
 * DEBUGGING: After uploading, open DevTools > Console and select a frame
 * color. Look for [TCP Mockup] log lines to see where the pipeline breaks.
 * Also run: tcpDebug() in the console for a full diagnostic report.
 * To test preview upload independently: tcpTestPreviewUpload()
 */


// =============================================================================
// LAYER DEFINITIONS
// =============================================================================

const TCP_CHAR_DEFS = {

  artboard:      { w: 1200, h: 1600 },
  assetBase:     'https://tcpvault.com/character-personalizer/files/web',
  manifestUrl:   'https://tcpvault.com/character-personalizer/manifest.php',
  nameAreaRatio: 0.19,

  frameOverlays: {
    finishOptionName: 'Finish',
    mockupBasePath: 'https://tcpvault.com/RESOURCES/MOCKUPS',
    map: {
      'Fine Art Print (Unframed)': {
        jpg: 'single_catalog_unframed_V.jpg',
        png: 'single_catalog_unframed_V.png',
        artRect: { x: .045, y: .07, w: .92, h: .89 },
      },
      'Natural Frame': {
        jpg: 'single_catalog-frame-natural_full_V.jpg',
        png: 'single_catalog-frame-natural_full_V.png',
        artRect: { x: .05, y: .065, w: .9, h: .87 },
      },
      'White Frame': {
        jpg: 'single_catalog-frame-white_full_V.jpg',
        png: 'single_catalog-frame-white_full_V.png',
        artRect: { x: .05, y: .065, w: .9, h: .87 },
      },
      'Black Frame': {
        jpg: 'single_catalog-frame-black_full_V.jpg',
        png: 'single_catalog-frame-black_full_V.png',
        artRect: { x: .05, y: .065, w: .9, h: .87 },
      },
    },
  },

   groups: [

    {
      prefix: 'background',
      id:     'bg',
      label:  'Background',
      step:   1,
      type:   'colorizable',
      colors: [
        { id: 'cream',  label: 'Cream',  hex: '#F5F0E8' },
        { id: 'blue',   label: 'Blue',   hex: '#88b5bdff' },
        { id: 'green',   label: 'Green',   hex: '#579466ff' },
        { id: 'mint',   label: 'Mint',   hex: '#A8D4C4' },
        { id: 'navy',   label: 'Navy',   hex: '#3A4A62' },
        { id: 'yellow', label: 'Yellow', hex: '#e2d480ff' },
      ],
    },

    {
      prefix: 'body',
      id:     'body',
      label:  'Character',
      step:   1,
      type:   'choice',
    },

    {
      prefix: 'hair',
      id:     'hair',
      label:  'Hair',
      step:   3,
      type:   'colorizable',
      colors: [
        { id: 'blonde',      label: 'Blonde',      hex: '#D4A843' },
        { id: 'light brown', label: 'Light Brown', hex: '#926a55ff' },
        { id: 'brown',       label: 'Brown',       hex: '#7A4A30' },
        { id: 'red',         label: 'Red',         hex: '#c0604bff' },
        { id: 'black',       label: 'Black',       hex: '#1d1a18ff' },
        { id: 'platinum',    label: 'Platinum',    hex: '#D8D0B8' },
        { id: 'silver',      label: 'Silver',      hex: '#B0B0C0' },
      ],
    },

    {
      prefix: 'clothing',
      id:     'clothing',
      label:  'Clothing',
      step:   5,
      type:   'colorizable',
      colors: [
        { id: 'pink',     label: 'Pink',     hex: '#E87090' },
        { id: 'red',      label: 'Red',      hex: '#c94b4bff' },
        { id: 'orange',   label: 'Orange',   hex: '#e4886cff' },
        { id: 'yellow',   label: 'Yellow',   hex: '#D4A843' },
        { id: 'green',    label: 'Green',    hex: '#669c71ff' },
        { id: 'blue',     label: 'Blue',     hex: '#609fc9ff' },
        { id: 'navy',     label: 'Navy',     hex: '#3f6086ff' },
        { id: 'purple',   label: 'Purple',   hex: '#8d90beff' },
        { id: 'brown',    label: 'Brown',    hex: '#906848' },
        { id: 'gray',     label: 'Gray',     hex: '#909098' },
      ],
    },

    {
      prefix:    'eyes',
      id:        'eyeColor',
      label:     'Eye color',
      step:      4,
      type:      'choice',
      displayAs: 'swatches',
      swatches: {
        'blue':  '#6B9FD4',
        'brown': '#8B5E3C',
        'hazel': '#7B9E5C',
        'dark':  '#2A1A10',
        'green': '#4A8A4A',
        'grey':  '#8A9AAA',
        'gray':  '#8A9AAA',
        'amber': '#C08030',
      },
    },


    {
      prefix: 'glasses',
      id:     'eyeAcc',
      label:  'Glasses',
      step:   4,
      type:   'optional-choice',
    },

    {
      prefix: 'hand',
      id:     'handAcc',
      label:  'Holding',
      step:   6,
      type:   'optional-choice',
    },

    {
      prefix: 'head',
      id:     'hairAcc',
      label:  'Hair accessory',
      step:   6,
      type:   'optional-choice',
    },

  ],

  stepLabels: {
    1: 'Character',
    3: 'Hair',
    4: 'Eyes',
    5: 'Clothing',
    6: 'Accessories',
  },

  nameColors: [
    { id: 'charcoal', label: 'Charcoal', hex: '#2E2A28' },
    { id: 'navy',     label: 'Navy',     hex: '#2A3A5A' },
    { id: 'white',    label: 'White',    hex: '#FFFFFF' },
    { id: 'blush',    label: 'Blush',    hex: '#C47A8A' },
    { id: 'sage',     label: 'Sage',     hex: '#4A6A4A' },
    { id: 'sand',     label: 'Sand',     hex: '#A08060' },
  ],

};


// =============================================================================
// MANIFEST PROCESSING
// =============================================================================

function tcpFilenameToLabel(filename, prefix, groupLabel) {
  var inner = filename
    .replace('tcp-char_' + prefix + '_', '')
    .replace('tcp-char_' + prefix, '')
    .replace('.png', '').trim();
  if (!inner) return groupLabel;
  if (/^\d+$/.test(inner)) {
    var np = { hair: 'Style', body: 'Person', clothing: 'Style' };
    return (np[prefix] || 'Option') + ' ' + inner;
  }
  return inner.split('-').map(function(w){ return w.charAt(0).toUpperCase()+w.slice(1); }).join(' ');
}

function tcpFilenameToId(filename, prefix) {
  return (filename
    .replace('tcp-char_' + prefix + '_', '')
    .replace('tcp-char_' + prefix, '')
    .replace('.png', '').trim()) || prefix;
}

function tcpGetSwatch(id, swatches) {
  if (!swatches) return null;
  if (swatches[id]) return swatches[id];
  var keys = Object.keys(swatches);
  for (var k = 0; k < keys.length; k++) {
    if (id.indexOf(keys[k]) !== -1 || keys[k].indexOf(id) !== -1) return swatches[keys[k]];
  }
  return null;
}

function tcpBuildConfig(manifestFiles, defs) {
  var layers = [], stepLayerMap = {};
  for (var g = 0; g < defs.groups.length; g++) {
    var gd = defs.groups[g];
    var files = manifestFiles[gd.prefix];
    if (!files || files.length === 0) continue;
    var options = files.map(function(fn) {
      var id = tcpFilenameToId(fn, gd.prefix);
      var label = tcpFilenameToLabel(fn, gd.prefix, gd.label);
      var opt = { id: id, label: label, src: fn };
      var swatch = tcpGetSwatch(id, gd.swatches);
      if (swatch) opt.swatch = swatch;
      return opt;
    });
    var type = gd.type;
    if (gd.prefix === 'body' && type === 'choice' && files.length === 1) type = 'static';
    var layer = { id: gd.id, type: type, label: gd.label };
    if (type === 'static') {
      layer.src = files[0];
    } else {
      if (gd.displayAs) layer.displayAs = gd.displayAs;
      if (type === 'colorizable') { layer.options=options; layer.colors=gd.colors; layer.default={style:options[0].id,color:gd.colors[0].id}; }
      else if (type === 'optional-choice') { layer.options=options; layer.default=null; }
      else { layer.options=options; layer.default=options[0].id; }
      if (gd.step) {
        if (!stepLayerMap[gd.step]) stepLayerMap[gd.step] = [];
        stepLayerMap[gd.step].push(gd.id);
      }
    }
    layers.push(layer);
  }
  var steps = [];
  var stepNums = Object.keys(stepLayerMap).map(Number).sort(function(a,b){return a-b;});
  for (var s = 0; s < stepNums.length; s++) {
    var num = stepNums[s];
    steps.push({ id:num, label:defs.stepLabels[num]||('Step '+num), type:'regular', layers:stepLayerMap[num] });
  }
  if (steps.length > 0) steps[0].type = 'start';
  var lastId = stepNums.length > 0 ? stepNums[stepNums.length-1] : 0;
  steps.push({ id:lastId+1, label:'Finish', type:'finish', layers:[] });
  return { artboard:defs.artboard, assetBase:defs.assetBase, nameAreaRatio:defs.nameAreaRatio, nameColors:defs.nameColors, layers:layers, steps:steps };
}


// =============================================================================
// DEFAULT STATE
// =============================================================================

function tcpCharDefaultState(config) {
  var state = { name:'', nameColor:'charcoal' };
  for (var i = 0; i < config.layers.length; i++) {
    var l = config.layers[i];
    if      (l.type==='choice')          state[l.id] = l.default;
    else if (l.type==='colorizable')      state[l.id] = Object.assign({}, l.default);
    else if (l.type==='optional-choice')  state[l.id] = null;
  }
  return state;
}


// =============================================================================
// HSL COLOR HELPERS
// =============================================================================

function tcpHexToHSL(hex) {
  var r=parseInt(hex.slice(1,3),16)/255, g=parseInt(hex.slice(3,5),16)/255, b=parseInt(hex.slice(5,7),16)/255;
  var max=Math.max(r,g,b), min=Math.min(r,g,b), h, s, l=(max+min)/2;
  if (max===min){h=s=0;}else{
    var d=max-min; s=l>0.5?d/(2-max-min):d/(max+min);
    switch(max){case r:h=((g-b)/d+(g<b?6:0))/6;break;case g:h=((b-r)/d+2)/6;break;case b:h=((r-g)/d+4)/6;break;}
  }
  return {h:h,s:s,l:l};
}

function tcpHSLToRGB(h, s, l) {
  var r,g,b;
  if(s===0){r=g=b=l;}else{
    function hue2rgb(p,q,t){if(t<0)t+=1;if(t>1)t-=1;if(t<1/6)return p+(q-p)*6*t;if(t<1/2)return q;if(t<2/3)return p+(q-p)*(2/3-t)*6;return p;}
    var q=l<0.5?l*(1+s):l+s-l*s, p=2*l-q;
    r=hue2rgb(p,q,h+1/3); g=hue2rgb(p,q,h); b=hue2rgb(p,q,h-1/3);
  }
  return {r:Math.round(r*255),g:Math.round(g*255),b:Math.round(b*255)};
}


// =============================================================================
// CANVAS COMPOSITOR
// =============================================================================

class TCPCharCompositor {
  constructor(canvas, config) {
    this.canvas = canvas;
    this.ctx    = canvas.getContext('2d');
    this.config = config;
    this.W = config.artboard.w;
    this.H = config.artboard.h;
    this._cache             = new Map();
    this._rendering         = false;
    this._pendingState      = null;
    this._lastRenderedState = null;
    this._mockup            = null;

    canvas.width  = this.W;
    canvas.height = this.H;

    this._colorCanvas       = document.createElement('canvas');
    this._colorCanvas.width  = this.W;
    this._colorCanvas.height = this.H;
    this._colorCtx = this._colorCanvas.getContext('2d', { willReadFrequently: true });

    this._artCanvas       = document.createElement('canvas');
    this._artCanvas.width  = this.W;
    this._artCanvas.height = this.H;
    this._artCtx = this._artCanvas.getContext('2d');
  }

  loadImg(src) {
    var url = (src.indexOf('http') === 0) ? src : this.config.assetBase + '/' + src;
    var self = this;
    if (this._cache.has(url)) return Promise.resolve(this._cache.get(url));
    return new Promise(function(resolve, reject) {
      var img = new Image();
      img.crossOrigin = 'anonymous';
      img.onload  = function() { self._cache.set(url, img); resolve(img); };
      img.onerror = function() { reject(new Error('Failed to load: ' + url)); };
      img.src = url;
    });
  }

  _drawCover(ctx, img, x, y, w, h) {
    var imgAspect = img.naturalWidth / img.naturalHeight;
    var targetAspect = w / h;
    var sx, sy, sw, sh;
    if (imgAspect > targetAspect) {
      sh = img.naturalHeight;
      sw = sh * targetAspect;
      sx = Math.round((img.naturalWidth - sw) / 2);
      sy = 0;
    } else {
      sw = img.naturalWidth;
      sh = Math.round(sw / targetAspect);
      sx = 0;
      sy = Math.round((img.naturalHeight - sh) / 2);
    }
    ctx.drawImage(img, sx, sy, sw, sh, x, y, w, h);
  }

  _drawContain(ctx, img, x, y, w, h) {
    var natW = img.naturalWidth  !== undefined ? img.naturalWidth  : img.width;
    var natH = img.naturalHeight !== undefined ? img.naturalHeight : img.height;
    var imgAspect = natW / natH;
    var targetAspect = w / h;
    var dw, dh;
    if (imgAspect > targetAspect) { dw = w; dh = w / imgAspect; }
    else                          { dh = h; dw = h * imgAspect; }
    ctx.drawImage(img, x + (w - dw) / 2, y + (h - dh) / 2, dw, dh);
  }

  async preloadStep(stepIndex) {
    var step = this.config.steps[stepIndex];
    if (!step) return;
    var srcs = [], self = this;
    for (var i = 0; i < step.layers.length; i++) {
      var def = this.config.layers.find(function(l){ return l.id === step.layers[i]; });
      if (def && def.options) for (var j = 0; j < def.options.length; j++) srcs.push(def.options[j].src);
    }
    await Promise.allSettled(srcs.map(function(s){ return self.loadImg(s).catch(function(){}); }));
  }

  setMockup(mockupDef) {
    console.log('[TCP Mockup] setMockup called:', mockupDef ? { jpg: mockupDef.jpg, png: mockupDef.png } : null);
    this._mockup = mockupDef;
    var self = this;

    var applyAndRender = function(newH) {
      if (newH && newH !== self.H) {
        self.H = newH;
        self.canvas.height       = newH;
        self._colorCanvas.height = newH;
        self._artCanvas.height   = newH;
        var wrap = self.canvas.parentElement;
        if (wrap) wrap.style.paddingBottom = (newH / self.W * 100) + '%';
        console.log('[TCP Mockup] Artboard resized to', self.W + 'x' + newH);
      }
      if (self._lastRenderedState) self.render(self._lastRenderedState);
    };

    if (mockupDef) {
      this.loadImg(mockupDef.jpg)
        .then(function(jpg) {
          applyAndRender(Math.round(self.W * jpg.naturalHeight / jpg.naturalWidth));
        })
        .catch(function() { applyAndRender(null); });
    } else {
      applyAndRender(self.config.artboard.h);
    }
  }

  async drawColorized(src, hexColor) {
    var W=this.W, H=this.H, img=await this.loadImg(src);
    this._colorCtx.clearRect(0,0,W,H);
    this._drawContain(this._colorCtx,img,0,0,W,H);
    var imageData=this._colorCtx.getImageData(0,0,W,H), data=imageData.data, target=tcpHexToHSL(hexColor);
    var totalLum=0, count=0;
    for(var j=0;j<data.length;j+=4){if(data[j+3]===0)continue;totalLum+=(data[j]*0.299+data[j+1]*0.587+data[j+2]*0.114)/255;count++;}
    var midLum=count>0?totalLum/count:0.4;
    for(var i=0;i<data.length;i+=4){
      if(data[i+3]===0)continue;
      var lum=(data[i]*0.299+data[i+1]*0.587+data[i+2]*0.114)/255;
      var new_l=lum<=midLum?(midLum>0?lum*(target.l/midLum):0):target.l+(lum-midLum)*((1-target.l)/(1-midLum));
      new_l=Math.max(0,Math.min(1,new_l));
      var rgb=tcpHSLToRGB(target.h,target.s,new_l);
      data[i]=rgb.r; data[i+1]=rgb.g; data[i+2]=rgb.b;
    }
    this._colorCtx.putImageData(imageData,0,0);
    this._drawContain(this.ctx,this._colorCanvas,0,0,W,H);
  }

  async drawName(state) {
    var ctx=this.ctx, W=this.W, H=this.H, config=this.config;
    await document.fonts.ready;

    var name          = state.name && state.name.trim();
    var isPlaceholder = !name;
    var displayName   = name || 'Name';

    var colorDef = config.nameColors.find(function(c){ return c.id===state.nameColor; });
    var nameHex  = isPlaceholder ? '#2E2A28' : (colorDef ? colorDef.hex : '#2E2A28');

    // available width for the name
    var nameY=Math.round(H*config.nameAreaRatio*0.65), maxW=W*0.70;
    ctx.textAlign='center'; ctx.textBaseline='middle'; ctx.fillStyle=nameHex;
    ctx.letterSpacing = '-0.03em';
    var size=Math.round(W*0.175);
    do{ctx.font='1000 '+size+'px \'YourPrintFont\', Helvetica, sans';size-=2;}
    while(ctx.measureText(displayName).width>maxW&&size>18);
    ctx.fillText(displayName,W/2,nameY);
  }

  async render(state) {
    if (this._rendering) { this._pendingState = state; return; }
    this._rendering = true;
    this._lastRenderedState = state;

    try {
      var ctx=this.ctx, W=this.W, H=this.H, config=this.config;
      ctx.clearRect(0,0,W,H);

      // ── Pass 1: background only ───────────────────────────────────────────
      // Background goes down first so the name strip sits on top of it,
      // but character layers (including hair) render after the strip.
      for (var i=0; i<config.layers.length; i++) {
        var def=config.layers[i];
        if (def.id !== 'bg') continue;
        try {
          ctx.save();
          ctx.globalCompositeOperation='source-over';
          if (def.type==='colorizable') {
            var sel=state[def.id];
            if(sel&&sel.style&&sel.color){
              var sOpt=def.options.find(function(o){return o.id===sel.style;});
              var cOpt=def.colors.find(function(c){return c.id===sel.color;});
              if(sOpt&&cOpt) await this.drawColorized(sOpt.src,cOpt.hex);
            }
          } else {
            var sel=state[def.id];
            var opt=def.options.find(function(o){return o.id===sel;});
            if(opt){var img=await this.loadImg(opt.src);this._drawContain(ctx,img,0,0,W,H);}
          }
          ctx.restore();
        } catch(layerErr){ ctx.restore(); console.warn('TCP Character: bg layer failed:',layerErr.message); }
      }

      // ── Name strip + text ─────────────────────────────────────────────────
      // Drawn before character layers so hair/accessories can overlap it.
      ctx.save();
      ctx.globalCompositeOperation='source-over';
      ctx.fillStyle='#FFFFFF';
      ctx.fillRect(0,0,W,Math.round(H*config.nameAreaRatio));
      ctx.restore();
      await this.drawName(state);

      // ── Pass 2: all character layers except background ────────────────────
      for (var i=0; i<config.layers.length; i++) {
        var def=config.layers[i];
        if (def.id === 'bg') continue; // already drawn in pass 1
        try {
          ctx.save();
          ctx.globalCompositeOperation='source-over';
          if (def.type==='static') {
            var img=await this.loadImg(def.src);
            this._drawContain(ctx,img,0,0,W,H);
          } else if (def.type==='choice') {
            var sel=state[def.id];
            var opt=def.options.find(function(o){return o.id===sel;});
            if(opt){var img=await this.loadImg(opt.src);this._drawContain(ctx,img,0,0,W,H);}
          } else if (def.type==='colorizable') {
            var sel=state[def.id];
            if(sel&&sel.style&&sel.color){
              var sOpt=def.options.find(function(o){return o.id===sel.style;});
              var cOpt=def.colors.find(function(c){return c.id===sel.color;});
              if(sOpt&&cOpt) await this.drawColorized(sOpt.src,cOpt.hex);
            }
          } else if (def.type==='optional-choice') {
            var sel=state[def.id];
            if(sel){var opt=def.options.find(function(o){return o.id===sel;});if(opt){var img=await this.loadImg(opt.src);this._drawContain(ctx,img,0,0,W,H);}}
          }
          ctx.restore();
        } catch(layerErr){
          ctx.restore();
          console.warn('TCP Character: layer "'+def.id+'" failed:',layerErr.message);
        }
      }

      if (this._mockup) {
        console.log('[TCP Mockup] Starting mockup composite...');
        try {
          this._artCtx.clearRect(0,0,W,H);
          this._artCtx.drawImage(this.canvas,0,0);
          ctx.clearRect(0,0,W,H);

          console.log('[TCP Mockup] Loading JPG:', this._mockup.jpg);
          var jpgImg = await this.loadImg(this._mockup.jpg);
          console.log('[TCP Mockup] JPG natural size:', jpgImg.naturalWidth + 'x' + jpgImg.naturalHeight);
          ctx.save();
          ctx.globalCompositeOperation='source-over';
          this._drawCover(ctx, jpgImg, 0, 0, W, H);
          ctx.restore();
          console.log('[TCP Mockup] JPG drawn OK');

          var ar = this._mockup.artRect || { x: 0, y: 0, w: 1, h: 1 };
          var artX = Math.round(ar.x * W);
          var artY = Math.round(ar.y * H);
          var artW = Math.round(ar.w * W);
          var artH = Math.round(ar.h * H);
          console.log('[TCP Mockup] Drawing art at:', {artX:artX, artY:artY, artW:artW, artH:artH});
          ctx.save();
          ctx.globalCompositeOperation='multiply';
          ctx.drawImage(this._artCanvas, artX, artY, artW, artH);
          ctx.restore();
          console.log('[TCP Mockup] Art (multiply) drawn OK');

          console.log('[TCP Mockup] Loading PNG:', this._mockup.png);
          var pngImg = await this.loadImg(this._mockup.png);
          ctx.save();
          ctx.globalCompositeOperation='source-over';
          this._drawCover(ctx, pngImg, 0, 0, W, H);
          ctx.restore();
          console.log('[TCP Mockup] PNG overlay drawn OK — composite complete');

        } catch(mockupErr) {
          console.error('[TCP Mockup] Composite FAILED:', mockupErr.message);
          console.error('[TCP Mockup] This is usually a CORS error. Check:');
          console.error('  1. public_html/RESOURCES/MOCKUPS/.htaccess has Access-Control-Allow-Origin: *');
          console.error('  2. Fetch this URL to test CORS:', this._mockup.jpg);
        }
      }

    } finally {
      this._rendering = false;
      if (this._pendingState) {
        var next=this._pendingState;
        this._pendingState=null;
        this.render(next);
      }
    }
  }

  exportPreview(quality) {
    return this.canvas.toDataURL('image/jpeg', quality || 0.88);
  }
}


// =============================================================================
// WIZARD UI
// =============================================================================

class TCPCharWizard {
  constructor({ canvasEl, uiEl, config, variantId }) {
    this.compositor  = new TCPCharCompositor(canvasEl, config);
    this.config      = config;
    this.uiEl        = uiEl;
    this.variantId   = variantId;
    this.stepIndex   = 0;
    this.state       = tcpCharDefaultState(config);
    this._cartLocked = true;
    this._thumbCache = new Map();

    window._tcpWizard = this;

    this._init();
  }

  // ── State persistence ─────────────────────────────────────────────────────

  _sessionKey() {
    // Product-specific key so different products don't share state
    var handle = window.location.pathname.split('/products/')[1] || 'default';
    return 'tcp-char-state-' + handle.split('?')[0];
  }

  _saveToSession() {
    try { sessionStorage.setItem(this._sessionKey(), JSON.stringify(this.state)); } catch(e) {}
  }

  _stateFromSession() {
    try {
      var saved = sessionStorage.getItem(this._sessionKey());
      return saved ? JSON.parse(saved) : null;
    } catch(e) { return null; }
  }

  // Reads ?tcp=<encoded> URL param — set by the cart page "Edit design" link
  _stateFromUrl() {
    try {
      var tcp = new URLSearchParams(window.location.search).get('tcp');
      return tcp ? JSON.parse(decodeURIComponent(tcp)) : null;
    } catch(e) { return null; }
  }

  _patch(changes) {
    Object.assign(this.state, changes);
    this.compositor.render(this.state);
    this._syncButtons();
    this._syncPropertiesToForm();
    this._syncCartLock();
    this._saveToSession();
  }

  _patchNested(key, changes) {
    this.state[key] = Object.assign({}, this.state[key], changes);
    this.compositor.render(this.state);
    this._syncButtons();
    this._syncPropertiesToForm();
    this._syncCartLock();
    this._saveToSession();
  }

  _syncButtons() {
    var self=this;
    this.uiEl.querySelectorAll('[data-tcp-char-opt]').forEach(function(btn){
      var sv=self.state[btn.dataset.tcpCharLayer];
      var cur=(typeof sv==='string'||sv===null)?sv:(sv?sv[btn.dataset.tcpCharKey]:null);
      btn.classList.toggle('tcp-char-selected',cur===btn.dataset.tcpCharOpt);
      btn.setAttribute('aria-pressed',String(cur===btn.dataset.tcpCharOpt));
    });
  }

  _syncCartLock() {
    if (this.state.name && this.state.name.trim()) {
      this._unlockAddToCart();
    } else {
      this._lockAddToCart();
    }
  }

  _lockAddToCart() {
    this._cartLocked=true;
    var form=document.querySelector('form[action*="/cart/add"]'); if(!form)return;
    var btn=form.querySelector('[name="add"]')||form.querySelector('.product-form__submit')||form.querySelector('[data-add-to-cart]');
    if(btn){btn.disabled=true;btn.setAttribute('aria-disabled','true');btn.dataset.tcpCharLocked='1';}
    if(!form.querySelector('.tcp-char-lock-msg')){
      var msg=document.createElement('p'); msg.className='tcp-char-lock-msg';
      msg.textContent = 'Enter a name above to add to cart.';
      var target=btn?(btn.closest('.product-form__controls')||btn.parentNode):form;
      target.insertAdjacentElement('afterend',msg);
    }
  }

  _unlockAddToCart() {
    this._cartLocked=false;
    var form=document.querySelector('form[action*="/cart/add"]'); if(!form)return;
    var btn=form.querySelector('[name="add"]')||form.querySelector('.product-form__submit')||form.querySelector('[data-add-to-cart]');
    if(btn){btn.disabled=false;btn.removeAttribute('aria-disabled');delete btn.dataset.tcpCharLocked;}
    form.querySelectorAll('.tcp-char-lock-msg').forEach(function(el){el.remove();});
  }

  _syncPropertiesToForm() {
    var form=document.querySelector('form[action*="/cart/add"]'); if(!form)return;
    form.querySelectorAll('[data-tcp-char-prop]').forEach(function(el){el.remove();});
    var s=this.state, cfg=this.config;
    function find(lid,val){
      var layer=cfg.layers.find(function(l){return l.id===lid;});
      if(!layer||!layer.options)return null;
      var opt=layer.options.find(function(o){return o.id===val;});
      return opt?opt.label:null;
    }
    var bgDef=cfg.layers.find(function(l){return l.id==='bg';});
    var bgColDef=bgDef&&s.bg?bgDef.colors.find(function(c){return c.id===s.bg.color;}):null;
    var hairDef=cfg.layers.find(function(l){return l.id==='hair';});
    var hairColDef=hairDef&&s.hair?hairDef.colors.find(function(c){return c.id===s.hair.color;}):null;
    var clothingDef=cfg.layers.find(function(l){return l.id==='clothing';});
    var clothingColDef=clothingDef&&s.clothing?clothingDef.colors.find(function(c){return c.id===s.clothing.color;}):null;
    var properties={
      'Name':s.name||'',
      'Character':find('body',s.body)||s.body||'',
      'Background':find('bg',s.bg&&s.bg.style)||(s.bg&&s.bg.style)||'',
      'Background color':bgColDef?bgColDef.label:(s.bg&&s.bg.color)||'',
      'Eye color':find('eyeColor',s.eyeColor)||s.eyeColor||'',
      'Hair style':find('hair',s.hair&&s.hair.style)||(s.hair&&s.hair.style)||'',
      'Hair color':hairColDef?hairColDef.label:(s.hair&&s.hair.color)||'',
      'Clothing style':find('clothing',s.clothing&&s.clothing.style)||(s.clothing&&s.clothing.style)||'',
      'Clothing color':clothingColDef?clothingColDef.label:(s.clothing&&s.clothing.color)||'',
      'Glasses':find('eyeAcc',s.eyeAcc)||'None',
      'Hair accessory':find('hairAcc',s.hairAcc)||'None',
      'Holding':find('handAcc',s.handAcc)||'None',
      'Name text color':(cfg.nameColors.find(function(c){return c.id===s.nameColor;})||{}).label||'',
      '_hex_hair':hairColDef?hairColDef.hex:'',
      '_hex_clothing':clothingColDef?clothingColDef.hex:'',
      '_hex_bg':bgColDef?bgColDef.hex:'',
    };
    Object.keys(properties).forEach(function(key){
      var input=document.createElement('input');
      input.type='hidden'; input.name='properties['+key+']';
      input.value=properties[key]; input.dataset.tcpCharProp='1';
      form.appendChild(input);
    });
  }

  _setupFrameListener() {
    var self=this;
    var fo=TCP_CHAR_DEFS.frameOverlays;
    if (!fo) { console.warn('[TCP Mockup] No frameOverlays config found'); return; }

    console.log('[TCP Mockup] Setting up frame listener...');
    console.log('[TCP Mockup] Config:', { finishOptionName: fo.finishOptionName, mockupBasePath: fo.mockupBasePath, mapKeys: Object.keys(fo.map) });

    var productDataEl=document.getElementById('tcp-char-product-data');
    if (!productDataEl) {
      console.error('[TCP Mockup] ✗ #tcp-char-product-data not found.');
      return;
    }
    console.log('[TCP Mockup] ✓ #tcp-char-product-data found');

    var product;
    try { product=JSON.parse(productDataEl.textContent); }
    catch(e) { console.error('[TCP Mockup] ✗ Could not parse product JSON:',e); return; }

    if (!product||!product.variants) { console.error('[TCP Mockup] ✗ No variants in product data'); return; }
    console.log('[TCP Mockup] ✓ Product loaded:', product.title);
    console.log('[TCP Mockup]   Options:', product.options);
    console.log('[TCP Mockup]   Variant count:', product.variants.length);
    console.log('[TCP Mockup]   First variant options:', product.variants[0]);

    var variantMap={};
    product.variants.forEach(function(variant){
      var opts={};
      if (product.options) product.options.forEach(function(optName,idx){ opts[optName]=variant['option'+(idx+1)]; });
      variantMap[variant.id]=opts;
    });

    function mockupForVariant(variantId) {
      var opts=variantMap[variantId];
      if (!opts) { console.warn('[TCP Mockup] No options found for variant ID:', variantId); return null; }
      var finish=opts[fo.finishOptionName];
      console.log('[TCP Mockup] Variant', variantId, '→ options:', opts);
      console.log('[TCP Mockup]   Finish field "'+fo.finishOptionName+'":', finish);
      if (!finish) { console.warn('[TCP Mockup] Finish option not found. Available keys:', Object.keys(opts)); return null; }
      var entry=fo.map[finish];
      if (!entry) { console.warn('[TCP Mockup] No map entry for "'+finish+'". Available keys:', Object.keys(fo.map)); return null; }
      var result = { jpg: fo.mockupBasePath+'/'+entry.jpg, png: fo.mockupBasePath+'/'+entry.png, artRect: entry.artRect||{x:0,y:0,w:1,h:1} };
      console.log('[TCP Mockup]   Resolved mockup:', result);
      return result;
    }

    function applyMockup(variantId) {
      console.log('[TCP Mockup] applyMockup called for variant:', variantId);
      self.compositor.setMockup(mockupForVariant(Number(variantId)));
    }

    var form=document.querySelector('form[action*="/cart/add"]');
    if (form) {
      var variantInput=form.querySelector('input[name="id"],select[name="id"]');
      console.log('[TCP Mockup] Initial variant input:', variantInput ? variantInput.value : 'NOT FOUND');
      if (variantInput&&variantInput.value) applyMockup(variantInput.value);
    } else { console.warn('[TCP Mockup] Cart form not found'); }

    if (form) {
      form.addEventListener('change',function(){
        var input=form.querySelector('input[name="id"],select[name="id"]');
        if (input&&input.value) applyMockup(input.value);
      });
    }

    document.addEventListener('variant:change',function(e){
      if (e.detail&&e.detail.variant) applyMockup(e.detail.variant.id);
    });

    document.addEventListener('variantChange',function(e){
      if (e.detail&&e.detail.variant) applyMockup(e.detail.variant.id);
    });

    if (form) {
      var hiddenInput=form.querySelector('input[name="id"]');
      if (hiddenInput&&window.MutationObserver) {
        new MutationObserver(function(mutations){
          mutations.forEach(function(m){
            if (m.type==='attributes'&&m.attributeName==='value') applyMockup(hiddenInput.value);
          });
        }).observe(hiddenInput,{attributes:true});
      }
    }

    console.log('[TCP Mockup] Frame listener setup complete');
  }

  _interceptNativeForm() {
    var self=this;
    var form=document.querySelector('form[action*="/cart/add"]'); if(!form)return;
    var uploadUrl = 'https://tcpvault.com/character-personalizer/save-preview.php';

    form.addEventListener('submit',function(e){
      e.preventDefault();
      if (self._cartLocked) {
        self.uiEl.scrollIntoView({behavior:'smooth',block:'start'});
        self.uiEl.style.outline='2px solid #c47a8a';
        setTimeout(function(){self.uiEl.style.outline='';},1200);
        return;
      }

      var previewDataUrl = self.compositor.exportPreview(0.72);
      var previewKB = Math.round(previewDataUrl.length * 0.75 / 1024);
      console.log('[TCP Preview] Exporting preview — size:', previewKB + 'KB', '| upload URL:', uploadUrl);

      // Store an "Edit design" URL as a line item property so the cart page
      // can link back to the product with all selections pre-filled.
      var editUrl = window.location.origin + window.location.pathname +
                    '?tcp=' + encodeURIComponent(JSON.stringify(self.state));
      form.querySelectorAll('[data-tcp-char-prop][name="properties[_edit_url]"]').forEach(function(el){el.remove();});
      var eu=document.createElement('input');
      eu.type='hidden'; eu.name='properties[_edit_url]';
      eu.value=editUrl; eu.dataset.tcpCharProp='1';
      form.appendChild(eu);

      fetch(uploadUrl, {
        method:  'POST',
        headers: { 'Content-Type': 'application/json' },
        body:    JSON.stringify({ image: previewDataUrl }),
      })
      .then(function(res){
        console.log('[TCP Preview] Upload response — HTTP', res.status, res.statusText);
        if (!res.ok) {
          return res.text().then(function(body){
            console.error('[TCP Preview] Server error body:', body || '(empty)');
            throw new Error('Upload HTTP ' + res.status);
          });
        }
        return res.text().then(function(raw){
          console.log('[TCP Preview] Raw server response:', raw);
          try {
            return JSON.parse(raw);
          } catch(e) {
            console.error('[TCP Preview] Could not parse JSON:', e.message, '| Raw:', raw);
            throw new Error('Invalid JSON from server');
          }
        });
      })
      .then(function(data){
        if (data.url) {
          console.log('[TCP Preview] ✓ Preview saved:', data.url);
          form.querySelectorAll('[data-tcp-char-prop][name="properties[_preview_url]"]').forEach(function(el){el.remove();});
          var pi=document.createElement('input');
          pi.type='hidden'; pi.name='properties[_preview_url]';
          pi.value=data.url; pi.dataset.tcpCharProp='1';
          form.appendChild(pi);
        } else {
          console.warn('[TCP Preview] ✗ Server returned no URL. Full response:', data);
        }
        console.log('[TCP Preview] Submitting cart...');
        return fetch('/cart/add.js',{method:'POST',body:new FormData(form)});
      })
      .then(function(res){
        if(res.ok){ window.location.href='/cart'; }
        else { return res.text().then(function(b){ throw new Error('Cart add HTTP '+res.status+': '+b); }); }
      })
      .catch(function(err){
        console.error('[TCP Preview] Submit failed:', err.message);
        console.warn('[TCP Preview] Falling back to direct form submit (no preview URL will be set)');
        form.submit();
      });
    });
  }

  _init() {
    var self=this;

    // Restore previous selections — URL param takes priority over session storage.
    // URL param is set by the cart page "Edit design" link.
    // Session storage handles page refresh and browser back/forward.
    var restored = this._stateFromUrl() || this._stateFromSession();
    if (restored) {
      // Merge into defaults so any new/missing keys are handled gracefully
      this.state = Object.assign(tcpCharDefaultState(this.config), restored);
    }

    this.compositor.render(this.state);
    this.compositor.preloadStep(0);
    this._renderStep();
    setTimeout(function(){
      self._syncCartLock();
      self._syncPropertiesToForm();
      self._interceptNativeForm();
      self._setupFrameListener();
    },300);
  }

  _go(delta) {
    this.stepIndex=Math.max(0,Math.min(this.config.steps.length-1,this.stepIndex+delta));
    this.compositor.preloadStep(this.stepIndex+1);
    this._renderStep();
    if (this.stepIndex < this.config.steps.length - 1) {
      this.uiEl.scrollIntoView({behavior:'smooth',block:'nearest'});
    }
  }

  _renderStep() {
    var self=this;
    var step=this.config.steps[this.stepIndex];
    var isFirst=this.stepIndex===0, isLast=this.stepIndex===this.config.steps.length-1;

    this._syncCartLock();
    this.uiEl.style.outline = '';

    var stepDots = '';
    for (var s = 0; s < this.config.steps.length; s++) {
      var cls = 'tcp-char-step-dot';
      if (s === this.stepIndex)    cls += ' tcp-char-step-dot--active';
      else if (s < this.stepIndex) cls += ' tcp-char-step-dot--done';
      stepDots += '<button class="'+cls+'" data-tcp-step-index="'+s+'" aria-label="'+this.config.steps[s].label+'">'+(s+1)+'</button>';
    }

    var prevStep = !isFirst ? this.config.steps[this.stepIndex - 1] : null;
    var nextStep = !isLast  ? this.config.steps[this.stepIndex + 1] : null;

    var navHTML=
      '<button class="tcp-char-btn tcp-char-btn-ghost" id="tcp-char-back"'+(isFirst?' disabled':'')+'>'+
        '&#8592; Back'+(prevStep ? ' ('+prevStep.label+')' : '')+
      '</button>'+
      (!isLast?
        '<button class="tcp-char-btn tcp-char-btn-primary" id="tcp-char-next">'+
          'Next ('+nextStep.label+') &#8594;'+
        '</button>':'');

    this.uiEl.innerHTML=
      '<div class="tcp-char-step-nav"><div class="tcp-char-step-track">'+stepDots+'</div></div>'+
      '<div class="tcp-char-step-header"><div class="tcp-char-step-meta">'+
        '<strong class="tcp-char-step-name">'+step.label+'</strong>'+
        '<span class="tcp-char-step-count">Step '+(this.stepIndex+1)+' of '+this.config.steps.length+'</span>'+
      '</div></div>'+
      '<div id="tcp-char-content" class="tcp-char-step-content"></div>'+
      '<div class="tcp-char-nav">'+navHTML+'</div>';

    this.uiEl.querySelectorAll('[data-tcp-step-index]').forEach(function(dot) {
      dot.addEventListener('click', function() {
        var idx = parseInt(dot.dataset.tcpStepIndex, 10);
        if (idx === self.stepIndex) return;
        self.stepIndex = idx;
        self.compositor.preloadStep(idx + 1);
        self._renderStep();
        if (idx < self.config.steps.length - 1) {
          self.uiEl.scrollIntoView({behavior:'smooth', block:'nearest'});
        }
      });
    });

    document.getElementById('tcp-char-back').addEventListener('click',function(){self._go(-1);});
    var nextBtn=document.getElementById('tcp-char-next');
    if(nextBtn) nextBtn.addEventListener('click',function(){self._go(1);});

    var content=document.getElementById('tcp-char-content');
    if(step.type==='start') content.appendChild(this._buildNameInputOnly());
    for(var i=0;i<step.layers.length;i++){
      var layerId=step.layers[i];
      var def=this.config.layers.find(function(l){return l.id===layerId;});
      if(!def)continue;
      // Always show sub-labels on the start step (name+character+background combined).
      // Otherwise suppress the label when it duplicates the step heading.
      var showLabel = step.layers.length > 1 &&
                      (step.type === 'start' || def.label.toLowerCase() !== step.label.toLowerCase());
      content.appendChild(this._buildPicker(def, showLabel));
    }
    if(step.type==='finish') content.appendChild(this._buildSummaryStep());
  }

  _buildPicker(def, showLabel) {
    var self=this;
    var wrap=document.createElement('div'); wrap.className='tcp-char-section';
    if(def.type==='choice'){
      if(showLabel !== false) wrap.appendChild(this._label(def.label));
      if(def.displayAs==='swatches'){
        wrap.appendChild(this._swatchRow(def.options,def.id,'id',function(id){var p={};p[def.id]=id;self._patch(p);}));
      }else{
        wrap.appendChild(this._thumbGrid(def,def.id,'id',function(id){var p={};p[def.id]=id;self._patch(p);}));
      }
    }else if(def.type==='optional-choice'){
      if(showLabel !== false) wrap.appendChild(this._label(def.label));
      var hint=document.createElement('p');hint.className='tcp-char-hint';hint.textContent='Tap again to deselect';
      wrap.appendChild(hint);
      wrap.appendChild(this._thumbGrid(def,def.id,'id',function(id){var p={};p[def.id]=self.state[def.id]===id?null:id;self._patch(p);}));
    }else if(def.type==='colorizable'){
      if(def.options.length > 1){
        // Multiple styles: outer label + thumbnail grid + 'Color' sub-label
        if(showLabel !== false) wrap.appendChild(this._label(def.label));
        wrap.appendChild(this._thumbGrid(def,def.id,'style',function(id){self._patchNested(def.id,{style:id});}));
        wrap.appendChild(this._label('Color'));
      } else {
        // Single style: combine into one label e.g. "Background Color"
        if(showLabel !== false) wrap.appendChild(this._label(def.label + ' Color'));
      }
      wrap.appendChild(this._swatchRow(def.colors,def.id,'color',function(id){self._patchNested(def.id,{color:id});}));
    }
    return wrap;
  }

  _label(text){var p=document.createElement('p');p.className='tcp-char-section-label';p.textContent=text;return p;}

  _autoCropThumbnail(imgEl, src) {
    var cacheKey = src;
    if (this._thumbCache.has(cacheKey)) {
      imgEl.src = this._thumbCache.get(cacheKey);
      return;
    }
    var self   = this;
    var outSize = 124;

    this.compositor.loadImg(src)
      .then(function(img) {
        var W = img.naturalWidth, H = img.naturalHeight;
        if (!W || !H) return;

        var sW = Math.max(1, Math.round(W / 10));
        var sH = Math.max(1, Math.round(H / 10));
        var sc = document.createElement('canvas');
        sc.width = sW; sc.height = sH;
        var sCtx = sc.getContext('2d', { willReadFrequently: true });
        sCtx.drawImage(img, 0, 0, sW, sH);
        var sd = sCtx.getImageData(0, 0, sW, sH).data;

        var minX = sW, minY = sH, maxX = 0, maxY = 0, found = false;
        for (var y = 0; y < sH; y++) {
          for (var x = 0; x < sW; x++) {
            if (sd[(y * sW + x) * 4 + 3] > 15) {
              if (x < minX) minX = x;
              if (y < minY) minY = y;
              if (x > maxX) maxX = x;
              if (y > maxY) maxY = y;
              found = true;
            }
          }
        }
        if (!found) return;

        var pad = 0.05;
        var sx = Math.max(0,  Math.floor((minX / sW - pad) * W));
        var sy = Math.max(0,  Math.floor((minY / sH - pad) * H));
        var ex = Math.min(W,  Math.ceil(((maxX + 1) / sW + pad) * W));
        var ey = Math.min(H,  Math.ceil(((maxY + 1) / sH + pad) * H));
        var cW = ex - sx, cH = ey - sy;
        if (cW <= 0 || cH <= 0) return;

        var out = document.createElement('canvas');
        out.width = outSize; out.height = outSize;
        var oCtx = out.getContext('2d');
        var scale = Math.min(outSize / cW, outSize / cH);
        var dW = cW * scale, dH = cH * scale;
        oCtx.drawImage(img, sx, sy, cW, cH,
          (outSize - dW) / 2, (outSize - dH) / 2, dW, dH);

        var dataUrl = out.toDataURL('image/png');
        self._thumbCache.set(cacheKey, dataUrl);
        if (imgEl.isConnected) imgEl.src = dataUrl;
      })
      .catch(function() {});
  }

  _thumbGrid(def,stateKey,nestedKey,onChange){
    var self=this;
    var grid=document.createElement('div');grid.className='tcp-char-thumb-grid';
    def.options.forEach(function(opt){
      var sv=self.state[stateKey],cur=(typeof sv==='string'||sv===null)?sv:(sv?sv[nestedKey]:null),on=cur===opt.id;
      var btn=document.createElement('button');
      btn.className='tcp-char-thumb-btn'+(on?' tcp-char-selected':'');
      btn.dataset.tcpCharOpt=opt.id;btn.dataset.tcpCharLayer=stateKey;btn.dataset.tcpCharKey=nestedKey;
      btn.setAttribute('aria-pressed',String(on));btn.title=opt.label;
      var img=document.createElement('img');
      img.crossOrigin='anonymous';img.src=self.config.assetBase+'/'+(opt.thumb||opt.src);
      img.alt=opt.label;img.loading='lazy';img.draggable=false;
      self._autoCropThumbnail(img, opt.thumb||opt.src);
      var lbl=document.createElement('span');lbl.textContent=opt.label;
      btn.appendChild(img);btn.appendChild(lbl);
      btn.addEventListener('click',function(){onChange(opt.id);});grid.appendChild(btn);
    });
    return grid;
  }

  _swatchRow(items,stateKey,nestedKey,onChange){
    var self=this;
    var row=document.createElement('div');row.className='tcp-char-color-row';
    items.forEach(function(item){
      var sv=self.state[stateKey],cur=nestedKey==='id'?(typeof sv==='string'?sv:null):(sv?sv[nestedKey]:null),on=cur===item.id;
      var btn=document.createElement('button');
      btn.className='tcp-char-dot-btn'+(on?' tcp-char-selected':'');
      btn.style.setProperty('--swatch',item.swatch||item.hex||'#ccc');
      btn.dataset.tcpCharOpt=item.id;btn.dataset.tcpCharLayer=stateKey;btn.dataset.tcpCharKey=nestedKey;
      btn.setAttribute('aria-label',item.label);btn.setAttribute('aria-pressed',String(on));btn.title=item.label;
      btn.addEventListener('click',function(){onChange(item.id);});row.appendChild(btn);
    });
    return row;
  }

  _buildNameInputOnly(){
    var self=this;
    var wrap=document.createElement('div');wrap.className='tcp-char-section';
    wrap.appendChild(this._label("Child's name"));
    var input=document.createElement('input');
    input.type='text';input.className='tcp-char-name-input';input.placeholder='Name';
    input.maxLength=16;input.value=this.state.name;input.autocomplete='off';input.spellcheck=false;
    input.addEventListener('input',function(e){self._patch({name:e.target.value});});
    wrap.appendChild(input);
    return wrap;
  }

  _buildSummaryStep(){
    var self=this;
    var wrap=document.createElement('div');wrap.className='tcp-char-section';
    wrap.appendChild(this._label('Your selections'));
    var ul=document.createElement('ul');ul.className='tcp-char-summary';
    if(this.state.name)ul.appendChild(this._li('Name',this.state.name));
    this.config.layers.forEach(function(def){
      if(def.type==='static')return;
      var val=self.state[def.id];if(!val)return;
      if(typeof val==='string'){
        var opt=def.options&&def.options.find(function(o){return o.id===val;});
        if(opt)ul.appendChild(self._li(def.label,opt.label));
      }else if(val.style){
        var opt=def.options&&def.options.find(function(o){return o.id===val.style;});
        var col=def.colors&&def.colors.find(function(c){return c.id===val.color;});
        if(opt&&col)ul.appendChild(self._li(def.label,opt.label+' - '+col.label));
      }
    });
    wrap.appendChild(ul);
    var note=document.createElement('p');note.className='tcp-char-preview-note';
    note.textContent='Your design is ready! Check the name spelling, then choose your size and format below.';
    wrap.appendChild(note);
    var scrollLink=document.createElement('a');
    scrollLink.href='#';scrollLink.className='tcp-char-scroll-link';scrollLink.textContent='Jump to size selection \u2193';
    scrollLink.addEventListener('click',function(e){
      e.preventDefault();
      var form=document.querySelector('form[action*="/cart/add"]');
      if(form)form.scrollIntoView({behavior:'smooth',block:'start'});
    });
    wrap.appendChild(scrollLink);
    return wrap;
  }

  _li(label,value){
    var li=document.createElement('li');
    var s=document.createElement('span');s.className='tcp-char-summary-label';s.textContent=label+':';
    li.appendChild(s);li.appendChild(document.createTextNode(' '+value));return li;
  }
}


// =============================================================================
// DIAGNOSTIC HELPERS
// =============================================================================

window.tcpDebug = function() {
  console.group('=== TCP Personalizer Diagnostic ===');
  var canvas = document.getElementById('tcp-char-canvas');
  console.log('Canvas:', canvas ? '✓ found' : '✗ not found');
  var productData = document.getElementById('tcp-char-product-data');
  console.log('Product JSON:', productData ? '✓ found' : '✗ MISSING');
  var form = document.querySelector('form[action*="/cart/add"]');
  console.log('Cart form:', form ? '✓ found' : '✗ not found');
  if (form) {
    var input = form.querySelector('input[name="id"],select[name="id"]');
    console.log('Variant input:', input ? '✓ value=' + input.value : '✗ not found');
  }
  var wizard = window._tcpWizard;
  console.log('Wizard:', wizard ? '✓ initialized' : '✗ not initialized');
  if (wizard) {
    console.log('Current mockup:', wizard.compositor._mockup);
    console.log('Cart locked:', wizard._cartLocked);
  }
  var fo = TCP_CHAR_DEFS.frameOverlays;
  if (fo) {
    var testUrl = fo.mockupBasePath + '/' + fo.map['Black Frame'].jpg;
    console.log('Testing mockup CORS...');
    fetch(testUrl, { mode: 'cors' })
      .then(function(r){ console.log('  Mockup CORS:', r.status, r.ok ? '✓ OK' : '✗ FAILED'); })
      .catch(function(e){ console.error('  Mockup CORS FAILED:', e.message); });
  }
  console.log('---');
  console.log('To test the preview upload separately, run: tcpTestPreviewUpload()');
  console.groupEnd();
};

window.tcpTestPreviewUpload = function() {
  var uploadUrl = 'https://tcpvault.com/character-personalizer/save-preview.php';
  var tinyJpeg = 'data:image/jpeg;base64,/9j/4AAQSkZJRgABAQAAAQABAAD/2wBDAAgGBgcGBQgHBwcJCQgKDBQNDAsLDBkSEw8UHRofHh0aHBwgJC4nICIsIxwcKDcpLDAxNDQ0Hyc5PTgyPC4zNDL/2wBDAQkJCQwLDBgNDRgyIRwhMjIyMjIyMjIyMjIyMjIyMjIyMjIyMjIyMjIyMjIyMjIyMjIyMjIyMjIyMjIyMjIyMjL/wAARCAABAAEDASIAAhEBAxEB/8QAFAABAAAAAAAAAAAAAAAAAAAACf/EABQQAQAAAAAAAAAAAAAAAAAAAAD/xAAUAQEAAAAAAAAAAAAAAAAAAAAA/8QAFBEBAAAAAAAAAAAAAAAAAAAAAP/aAAwDAQACEQMRAD8AJQAB/9k=';
  console.group('=== TCP Preview Upload Test ===');
  console.log('Endpoint:', uploadUrl);
  console.log('Sending 1×1 test JPEG...');
  fetch(uploadUrl, { method:'POST', headers:{'Content-Type':'application/json'}, body:JSON.stringify({image:tinyJpeg}) })
  .then(function(res){
    console.log('HTTP status:', res.status, res.statusText);
    res.headers.forEach(function(v,k){console.log(' ',k+':',v);});
    return res.text();
  })
  .then(function(raw){
    console.log('Raw response body:', raw||'(empty)');
    try {
      var data=JSON.parse(raw);
      if(data.url){console.log('✓ SUCCESS — preview URL:',data.url);}
      else if(data.error){console.error('✗ Server returned error:',data.error);}
      else{console.warn('✗ Unexpected response:',data);}
    }catch(e){
      console.error('✗ Response is not valid JSON:',e.message);
      console.error('  Check that save-preview.php is at:',uploadUrl);
    }
  })
  .catch(function(err){
    console.error('✗ Fetch failed:',err.message);
    console.error('  1. CORS not enabled on tcpvault.com/character-personalizer/');
    console.error('  2. Network error or SSL issue');
  })
  .finally(function(){console.groupEnd();});
};

console.log('[TCP Character] Loaded. Run tcpDebug() or tcpTestPreviewUpload() in console for diagnostics.');


// =============================================================================
// INIT
// =============================================================================

document.addEventListener('DOMContentLoaded',function(){
  var canvasEl  = document.getElementById('tcp-char-canvas');
  var uiEl      = document.getElementById('tcp-char-wizard-ui');
  var variantEl = document.getElementById('tcp-char-variant-id');
  if (!canvasEl||!uiEl) return;

  uiEl.innerHTML='<p class="tcp-char-section-label" style="padding:16px 0;">Loading...</p>';

  fetch(TCP_CHAR_DEFS.manifestUrl)
    .then(function(res){if(!res.ok)throw new Error('Manifest HTTP '+res.status);return res.json();})
    .then(function(manifest){
      var config=tcpBuildConfig(manifest.files,TCP_CHAR_DEFS);
      if(config.layers.length===0){
        uiEl.innerHTML='<p style="color:#c44;padding:16px;">No layer files found.</p>';
        return;
      }
      new TCPCharWizard({canvasEl:canvasEl,uiEl:uiEl,config:config,variantId:variantEl?Number(variantEl.value):null});
    })
    .catch(function(err){
      console.error('[TCP Character] Manifest error:',err);
      uiEl.innerHTML='<p style="color:#c44;padding:16px;">Could not load personalizer. Please refresh the page.</p>';
    });
});